
import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

export interface Country {
  code: string;
  name: string;
}

export interface Constituency {
  code: string;
  name: string;
  country_code: string;
}

interface Props {
  countries: Country[];
  countryCode: string;
  setCountryCode: (code: string) => void;
  constituencyCode: string;
  setConstituencyCode: (code: string) => void;
  phoneNumber: string;
  setPhoneNumber: (p: string) => void;
}

const CreateUserLocationFields: React.FC<Props> = ({
  countries,
  countryCode,
  setCountryCode,
  constituencyCode,
  setConstituencyCode,
  phoneNumber,
  setPhoneNumber,
}) => {
  const [constituencies, setConstituencies] = useState<Constituency[]>([]);
  const [loadingConstituencies, setLoadingConstituencies] = useState(false);

  // Fetch constituencies when country changes
  useEffect(() => {
    if (!countryCode) {
      setConstituencies([]);
      return;
    }
    
    const fetchConstituencies = async () => {
      setLoadingConstituencies(true);
      console.log("Fetching constituencies for country code:", countryCode);
      
      try {
        const { data, error } = await supabase
          .from("constituencies")
          .select("code, name, country_code")
          .eq("country_code", countryCode)
          .eq("active", true)
          .order("name");

        if (error) {
          console.error("Error fetching constituencies:", error);
          throw error;
        }
        
        console.log("Fetched constituencies:", data?.length || 0);
        setConstituencies(data || []);
      } catch (error) {
        console.error("Error in constituency fetch:", error);
        setConstituencies([]);
      } finally {
        setLoadingConstituencies(false);
      }
    };

    fetchConstituencies();
  }, [countryCode]);

  const handleCountryChange = (value: string) => {
    // Only update if the value is different to avoid unnecessary renders
    if (value !== countryCode) {
      console.log("Country changed to:", value);
      setCountryCode(value);
      // Clear constituency when country changes
      if (constituencyCode) {
        setConstituencyCode("");
      }
    }
  };

  const handleConstituencyChange = (value: string) => {
    // Only update if the value is different to avoid unnecessary renders
    if (value !== constituencyCode) {
      console.log("Constituency changed to:", value);
      setConstituencyCode(value);
    }
  };

  return (
    <>
      <div className="space-y-2">
        <Label className="mb-1">Country</Label>
        <Select
          value={countryCode}
          onValueChange={handleCountryChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a country" />
          </SelectTrigger>
          <SelectContent>
            {countries.map(c => (
              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label className="mb-1">Constituency</Label>
        <Select
          value={constituencyCode}
          onValueChange={handleConstituencyChange}
          disabled={!countryCode || loadingConstituencies || constituencies.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder={
              loadingConstituencies
                ? "Loading..."
                : !countryCode
                  ? "Select a country first"
                  : constituencies.length === 0
                    ? "No constituencies available"
                    : "Select a constituency"
            } />
          </SelectTrigger>
          <SelectContent>
            {constituencies.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label className="mb-1">Phone Number</Label>
        <Input
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="+1234567890"
        />
      </div>
    </>
  );
};

export default CreateUserLocationFields;
